import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, BedDouble, CalendarCheck, MessageSquare, 
  Users, DollarSign, Loader2, AlertCircle, LogOut, Settings, Layers, Percent, Shield, Image, Mail, Webhook
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { adminApi } from '../../services/api';
import { supabase } from '../../supabaseClient';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated, signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total_bookings: 0,
    pending_bookings: 0,
    confirmed_bookings: 0,
    total_revenue: 0,
    total_rooms: 0,
    pending_reviews: 0
  });

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        setError('Please log in to access admin dashboard');
        return;
      }
      
      const data = await adminApi.getDashboard(token);
      setStats(data);
    } catch (err) {
      console.error('Dashboard error:', err);
      if (err.message.includes('403') || err.message.includes('Admin')) {
        setError('You do not have admin access. Please contact support.');
      } else {
        setError(err.message || 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDashboard();
  }, [isAuthenticated, navigate, fetchDashboard]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin" size={40} style={{ color: 'var(--olive-primary)' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="container max-w-4xl py-16">
          <div className="p-8 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
            <AlertCircle size={48} className="mx-auto mb-4" style={{ color: '#dc2626' }} />
            <h1 className="heading-2 mb-4">Access Denied</h1>
            <p className="body-regular mb-8" style={{ color: 'var(--text-secondary)' }}>{error}</p>
            <div className="flex gap-4 justify-center">
              <Link to="/">
                <Button className="btn-primary">Back to Home</Button>
              </Link>
              <Button variant="outline" onClick={handleLogout} style={{ borderRadius: 0 }}>
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="admin-dashboard">
      <div className="container py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="hero-medium mb-2">Admin Dashboard</h1>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              Welcome back, {user?.email}
            </p>
          </div>
          <Button variant="outline" onClick={handleLogout} style={{ borderRadius: 0 }}>
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="p-6" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }} data-testid="stat-bookings">
            <div className="flex items-center gap-4">
              <div className="p-3" style={{ backgroundColor: 'var(--interactive-base)', color: 'var(--bg-primary)' }}>
                <CalendarCheck size={24} />
              </div>
              <div>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Total Bookings</p>
                <p className="heading-2">{stats.total_bookings}</p>
              </div>
            </div>
          </div>

          <div className="p-6" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }} data-testid="stat-pending">
            <div className="flex items-center gap-4">
              <div className="p-3" style={{ backgroundColor: '#eab308', color: 'var(--bg-primary)' }}>
                <CalendarCheck size={24} />
              </div>
              <div>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Pending Bookings</p>
                <p className="heading-2">{stats.pending_bookings}</p>
              </div>
            </div>
          </div>

          <div className="p-6" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }} data-testid="stat-revenue">
            <div className="flex items-center gap-4">
              <div className="p-3" style={{ backgroundColor: '#22c55e', color: 'var(--bg-primary)' }}>
                <DollarSign size={24} />
              </div>
              <div>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Total Revenue</p>
                <p className="heading-2">${stats.total_revenue.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="p-6" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }} data-testid="stat-reviews">
            <div className="flex items-center gap-4">
              <div className="p-3" style={{ backgroundColor: '#ef4444', color: 'var(--bg-primary)' }}>
                <MessageSquare size={24} />
              </div>
              <div>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Pending Reviews</p>
                <p className="heading-2">{stats.pending_reviews}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <h2 className="heading-2 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Link to="/admin/rooms" data-testid="admin-rooms-link">
            <div className="p-8 hover-lift cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <BedDouble size={32} className="mb-4" style={{ color: 'var(--text-primary)' }} />
              <h3 className="heading-3 mb-2">Manage Rooms</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {stats.total_rooms} rooms available
              </p>
            </div>
          </Link>

          <Link to="/admin/bookings" data-testid="admin-bookings-link">
            <div className="p-8 hover-lift cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <CalendarCheck size={32} className="mb-4" style={{ color: 'var(--text-primary)' }} />
              <h3 className="heading-3 mb-2">Manage Bookings</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {stats.pending_bookings} pending, {stats.confirmed_bookings} confirmed
              </p>
            </div>
          </Link>

          <Link to="/admin/reviews" data-testid="admin-reviews-link">
            <div className="p-8 hover-lift cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <MessageSquare size={32} className="mb-4" style={{ color: 'var(--text-primary)' }} />
              <h3 className="heading-3 mb-2">Manage Reviews</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {stats.pending_reviews} awaiting approval
              </p>
            </div>
          </Link>
        </div>

        {/* Billing Configuration */}
        <h2 className="heading-2 mb-6">Billing Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Link to="/admin/room-types" data-testid="admin-room-types-link">
            <div className="p-8 hover-lift cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <Layers size={32} className="mb-4" style={{ color: 'var(--olive-primary)' }} />
              <h3 className="heading-3 mb-2">Room Types & Pricing</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Define room types with base rates and child pricing rules
              </p>
            </div>
          </Link>

          <Link to="/admin/billing" data-testid="admin-billing-link">
            <div className="p-8 hover-lift cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <Percent size={32} className="mb-4" style={{ color: 'var(--olive-primary)' }} />
              <h3 className="heading-3 mb-2">Taxes & Promotions</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Configure tax rules and promotional discounts
              </p>
            </div>
          </Link>
        </div>

        {/* System Administration */}
        <h2 className="heading-2 mb-6">System Administration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <Link to="/admin/users" data-testid="admin-users-link">
            <div className="p-8 hover-lift cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <Shield size={32} className="mb-4" style={{ color: 'var(--olive-primary)' }} />
              <h3 className="heading-3 mb-2">Manage Administrators</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Add or remove admin access for users
              </p>
            </div>
          </Link>

          <Link to="/admin/gallery" data-testid="admin-gallery-link">
            <div className="p-8 hover-lift cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <Image size={32} className="mb-4" style={{ color: 'var(--olive-primary)' }} />
              <h3 className="heading-3 mb-2">Gallery Management</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Manage gallery categories and images
              </p>
            </div>
          </Link>

          <Link to="/admin/email-settings" data-testid="admin-email-settings-link">
            <div className="p-8 hover-lift cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <Mail size={32} className="mb-4" style={{ color: 'var(--olive-primary)' }} />
              <h3 className="heading-3 mb-2">Email Settings</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Configure email provider (Resend or SMTP)
              </p>
            </div>
          </Link>

          <Link to="/admin/webhooks" data-testid="admin-webhooks-link">
            <div className="p-8 hover-lift cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <Webhook size={32} className="mb-4" style={{ color: 'var(--olive-primary)' }} />
              <h3 className="heading-3 mb-2">Webhook Settings</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Sync bookings with your PMS system
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
